import React, { useCallback, useContext, useEffect, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpIcon from "@mui/icons-material/Help";
import {
  Box,
  Button,
  Link as ExternalLink,
  Grid,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, useLocation, useParams } from "react-router-dom";

import { HardwareSensor } from "@safetyculture/s12-apis-connect-web/s12/iot/admin/v1/sensors_pb";
import { Asset_ExistsResult } from "@safetyculture/s12-apis-connect-web/s12/iot/v1/asset_pb";
import { Gateway } from "@safetyculture/s12-apis-connect-web/s12/sensors/v1/gateway_pb";

import DatePicker from "../components/shared/date-picker";
import { SensorAlertCSVExportURL } from "../constants/sensors";
import { toUUID } from "../helpers/s12-helpers";
import { UrlParamTypes } from "../types/org-display";
import {
  downloadSensorAlertHistoryForOrg as downloadSensorAlertHistoryForOrgAPI,
  listGatewaysForOrg as listGatewaysForOrgAPI,
  listHardwareSensorsForOrg as listHardwareSensorsForOrgAPI,
} from "api/endpoints/sensors";
import { callOptionsHeaders } from "api/helpers";
import OrganisationGatewaysTable from "components/sensors/org-gateways-table";
import OrganisationSensorsTable from "components/sensors/org-sensors-table";
import {
  SensorIndexToTab,
  SensorTabType,
  getTabIndex,
  sensorTabList,
  sensorTabToDisplayName,
} from "components/sensors/sensor-tab-utils";
import { LocationState } from "components/sensors/types";
import { SnackbarContext } from "context/snackbar-context";

export function displaySensorDetails(
  existsResult: Asset_ExistsResult
): boolean {
  return (
    existsResult == Asset_ExistsResult.ASSET_ACTIVE_ASSET_EXISTS ||
    existsResult == Asset_ExistsResult.ASSET_ARCHIVED_ASSET_EXISTS
  );
}

const OrganisationSensors = () => {
  const theme = useTheme();
  const location = useLocation<LocationState>();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const { orgName } = location.state || { orgName: "" };
  const [isLoading, setIsLoading] = useState(false);

  const urlParams = new URLSearchParams(location.search);
  const tabParam = urlParams.get("tab") as SensorTabType;
  const initialTabType = Object.values(SensorTabType).includes(tabParam)
    ? tabParam
    : SensorTabType.SENSORS;

  const [activeTab, setActiveTab] = useState<SensorTabType>(initialTabType);
  const [sensorsList, setSensorsList] = useState<HardwareSensor[]>([]);
  const [gatewaysList, setGatewaysList] = useState<Gateway[]>([]);
  const [fromDate, setFromDate] = useState<Date>(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  );
  const [toDate, setToDate] = useState<Date>(new Date());

  const { showError } = useContext(SnackbarContext);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newTabType = SensorIndexToTab[newValue];
    if (newTabType) {
      setActiveTab(newTabType);
    }
  };

  const listHardwareSensorsForOrg = async (roleId: string) => {
    setIsLoading(true);
    return listHardwareSensorsForOrgAPI(
      {
        orgId: roleId,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((res) => {
        setSensorsList(res.sensors);
        setIsLoading(false);
      })
      .catch((error) => {
        showError(error);
        setIsLoading(false);
      });
  };

  const listGatewaysForOrg = async (roleId: string) => {
    setIsLoading(true);
    return listGatewaysForOrgAPI(
      {
        orgId: roleId,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((res) => {
        setGatewaysList(res.gateways);
        setIsLoading(false);
      })
      .catch((error) => {
        showError(error);
        setIsLoading(false);
      });
  };

  const CustomFooter = () => {
    return (
      <Box
        sx={{
          padding: "10px",
          display: "flex",
          justifyContent: "left",
          alignItems: "center",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <ExternalLink
          href="https://help.safetyculture.com/en-US/000107/"
          target="_blank"
          color="primary"
          rel="noopener noreferrer"
        >
          Sensor & Gateway Troubleshooting
        </ExternalLink>
      </Box>
    );
  };

  const downloadAlertEventsForOrg = async (
    roleId: string,
    fromDate: Date,
    toDate: Date
  ) => {
    setIsLoading(true);
    const orgID = toUUID(roleId);

    return downloadSensorAlertHistoryForOrgAPI(
      {
        orgId: orgID,
        fromDate,
        toDate,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((alertHistoryURL) => {
        window.location.assign(alertHistoryURL);
        setIsLoading(false);
      })
      .catch((error) => {
        showError(error);
        setIsLoading(false);
      });
  };

  const renderTab = (tabType: SensorTabType) => {
    switch (tabType) {
      case SensorTabType.SENSORS:
        return (
          <>
            <Grid container direction={"row"} justifyContent="flex-end">
              <Grid item xs={4} />
              <Grid item sm={8}>
                <DatePicker
                  label="From"
                  size="small"
                  defaultDateSelection={fromDate}
                  maxDate={toDate}
                  onChange={(date) => {
                    setFromDate(date);
                  }}
                />
                &nbsp;
                <DatePicker
                  label="To"
                  size="small"
                  defaultDateSelection={toDate}
                  onChange={(date) => {
                    setToDate(date);
                  }}
                />
                &nbsp;
                <Tooltip title="Get all sensor alerts for Org">
                  <Button
                    onClick={() => {
                      downloadAlertEventsForOrg(roleId, fromDate, toDate);
                    }}
                    variant="contained"
                  >
                    Download Sensor Alerts
                  </Button>
                </Tooltip>
                <a
                  href={SensorAlertCSVExportURL}
                  target={"_blank"}
                  rel={"noreferrer"}
                >
                  <HelpIcon
                    style={{
                      width: "20px",
                      verticalAlign: "middle",
                      marginLeft: "8px",
                      color: theme.palette.primary.main,
                    }}
                  />
                </a>
              </Grid>
            </Grid>
            <OrganisationSensorsTable
              sensorsList={sensorsList}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          </>
        );
      case SensorTabType.GATEWAYS:
        return (
          <OrganisationGatewaysTable
            gatewaysList={gatewaysList}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        );

      default:
        return null;
    }
  };

  const getHardwareSensorsList = useCallback(listHardwareSensorsForOrg, [
    regionCode,
    showError,
  ]);
  useEffect(() => {
    getHardwareSensorsList(roleId);
  }, [getHardwareSensorsList, roleId]);

  const getGatewaysList = useCallback(listGatewaysForOrg, [
    regionCode,
    showError,
  ]);
  useEffect(() => {
    getGatewaysList(roleId);
  }, [getGatewaysList, roleId]);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ paddingBottom: "20px" }}>
          <Grid container alignContent="space-between" alignItems="center">
            <Link
              to={`/organisation/${regionCode}/${roleId}`}
              className="remove-link-style"
              style={{ position: "relative", top: "35px" }}
            >
              <Button startIcon={<ArrowBackIcon />} color="primary">
                Back
              </Button>
            </Link>

            <Grid item xs={12}>
              <Typography
                variant="h5"
                color="textPrimary"
                gutterBottom
                data-testid="organisation-history-title"
              >
                {orgName && `${orgName} - `}
                Organisation {sensorTabToDisplayName[activeTab]}
              </Typography>
            </Grid>
          </Grid>
          <br />
          <Tabs
            value={getTabIndex(activeTab)}
            onChange={handleTabChange}
            aria-label="Sensor and Gateway Example"
          >
            {sensorTabList.map((tabType) => (
              <Tab
                key={tabType}
                label={sensorTabToDisplayName[tabType]}
                value={getTabIndex(tabType)}
              />
            ))}
          </Tabs>

          {renderTab(activeTab)}
        </Grid>
      </Grid>
      {CustomFooter()}
    </>
  );
};
export default OrganisationSensors;
